import type { FilterOption, SelectedFilterValue } from '@/components/shared';

/**
 * Industry → Sub-industry hierarchy (NPJB-334). Mirrors the geo region→country
 * shape, but the tree is 1:1 (each Level-2 sub-industry has exactly one Level-1
 * parent), so the sync is simpler than geo's M:N cascade: we only prune children
 * when a parent is deselected, and never auto-add them (parent-only = "all
 * children", expanded server-side).
 */
export interface IndustryHierarchy {
  /** Level-1 industries (the parent dropdown). */
  parentOptions: FilterOption[];
  /** Level-2 sub-industries; each carries `parentId` = its parent's value. */
  childOptions: FilterOption[];
}

/** Sub-industries under one parent heading — the grouped Sub-industry dropdown. */
export interface IndustryOptionGroup {
  key: string;
  label: string;
  options: FilterOption[];
}

interface IndustryLookups {
  childrenByParentValue: Map<string, FilterOption[]>;
  parentValueByChildValue: Map<string, string>;
}

export function buildIndustryLookups(hierarchy: IndustryHierarchy): IndustryLookups {
  const childrenByParentValue = new Map<string, FilterOption[]>();
  const parentValueByChildValue = new Map<string, string>();
  for (const child of hierarchy.childOptions) {
    if (!child.parentId) continue;
    const list = childrenByParentValue.get(child.parentId);
    if (list) list.push(child);
    else childrenByParentValue.set(child.parentId, [child]);
    parentValueByChildValue.set(child.value, child.parentId);
  }
  return { childrenByParentValue, parentValueByChildValue };
}

/**
 * Sub-industry options grouped under their parent heading, for the currently
 * selected parent industries. Parents with no children are skipped. Drives the
 * grouped Sub-industry dropdown; empty result → the dropdown is disabled.
 */
export function subOptionGroupsForParents(
  selectedParents: SelectedFilterValue[],
  lookups: IndustryLookups,
): IndustryOptionGroup[] {
  const groups: IndustryOptionGroup[] = [];
  for (const parent of selectedParents) {
    const options = lookups.childrenByParentValue.get(parent.value) ?? [];
    if (options.length > 0) {
      groups.push({ key: parent.value, label: parent.label, options });
    }
  }
  return groups;
}

/**
 * When the selected parent industries change, drop any selected sub-industry whose parent
 * is no longer selected (or that isn't in the taxonomy). Returns the synced industry +
 * sub-industry slices.
 *
 * The taxonomy lookup is the keep/drop test, which is only safe because Sub-industry is a
 * closed taxonomy (`allowCustom: false` in FILTER_CONFIGS) — every legitimate sub resolves
 * to a parent. NPJB-608 nearly opened it to custom values; had it landed, this predicate
 * would have silently deleted every typed sub on the next Industry edit, since a custom sub
 * is by definition absent from the lookup. Reopening it means revisiting this line first.
 */
export function syncIndustryChange(
  newParents: SelectedFilterValue[],
  currentSubs: SelectedFilterValue[],
  lookups: IndustryLookups,
): { industry: SelectedFilterValue[]; subIndustry: SelectedFilterValue[] } {
  const selectedParentValues = new Set(newParents.map((p) => p.value));
  const subIndustry = currentSubs.filter((sub) => {
    const parentValue = lookups.parentValueByChildValue.get(sub.value);
    return parentValue !== undefined && selectedParentValues.has(parentValue);
  });
  return { industry: newParents, subIndustry };
}
